import { SessionStateHelper } from './session-state.helper';
import { of, from, switchMap, tap, catchError, throwError, forkJoin, finalize } from 'rxjs';
import * as _ from 'lodash';
import { InsightsModel } from 'src/app/common/models/common.model';
import { SelectionSummaryHelper } from '../content/selection-summary.helper';
import { MarkdownPayloadHelper } from '../content/markdown-payload.helper';

/** Base-type labels used when building the snapshot selection summary. */
const BASE_TYPES = {
  TOTAL: 'Total Base',
  ANSWER: 'Answer Base'
} as const;

/**
 * OrchestrationHelper
 *
 * The Step 1 (orchestration) -> Step 2 (analytical payload) -> Step 3 (render) pipeline
 * that drives every AskMe question, for both fresh queries and replayed chat history.
 *
 * Methods:
 * 1. continueProcessingMessage() - Run the full orchestration/analysis/render pipeline
 */
export class OrchestrationHelper {

  static continueProcessingMessage(comp: any, ChatResponse: any, isChatResponse: boolean, targetBlock: any = null) {
    if (!targetBlock && !isChatResponse && comp.chatPreviewBlocks.length > 0) {
      targetBlock = comp.chatPreviewBlocks[comp.chatPreviewBlocks.length - 1];
    }

    if (comp.inputValue !== '' || isChatResponse || true) { // Always true if called here
      // History saving logic for previous block if needed
      if (comp.chatResponse && comp.chatResponse.length > 0 && comp.chatPreviewBlocks.length > 1) {
        const lastQuestion = comp.chatPreviewBlocks[comp.chatPreviewBlocks.length - 2];
        const questionAnswer = lastQuestion.QuestionAnswer || comp.defaultQuestionAnswer;

        comp.saveChatList(
          lastQuestion.question,
          comp.chatResponse,
          questionAnswer,
          lastQuestion.messageId
        );
      }
      comp.setFailedCaseState(false, targetBlock);
      comp.chatResponse = [];
      let AgentPayload: any = null;
      let getSnapshotConfiguration: any = null;
      let GetCustomSnapshotWidget: any = null;
      let GetQuery: any = null;
      let getSnapshotOutput: any = null;
      let getSnapshotLoadData: any = null;
      let getConfiguration: any = null;
      let getSnapshotInsights: any = null;
      let getSnapshotInsightsTrend: any = null;
      let getInsights: any = null;
      let getQnA: any = null;
      let parsedHistory: any = null;

      if (isChatResponse) {
        const parsed = comp.parseChatResponse(ChatResponse);
        parsedHistory = parsed;

        AgentPayload = parsed['AgentPayload'] || parsed['GetMcode'];
        if (typeof AgentPayload === 'number' || !AgentPayload) {
          AgentPayload = { event: 'final_result', data: ChatResponse, isChatResponse: true, messageId: comp.selectedMessageId, member_responses: parsed['member_responses'] };
        } else if (!AgentPayload.event) {
          const originalPayload = AgentPayload;
          AgentPayload = { event: 'final_result', data: ChatResponse, isChatResponse: true, messageId: comp.selectedMessageId, member_responses: parsed['member_responses'], promptData: originalPayload.promptData || originalPayload };
        }
        getSnapshotConfiguration = parsed['getSnapshotConfiguration'];
        GetCustomSnapshotWidget = parsed['GetCustomSnapshotWidget'];
        GetQuery = parsed['GetQuery'];
        getSnapshotOutput = parsed['getSnapshotOutput'];
        getSnapshotLoadData = parsed['getSnapshotLoadData'];
        getConfiguration = parsed['getConfiguration'];
        getSnapshotInsights = parsed['getSnapshotInsights'];
        getSnapshotInsightsTrend = parsed.getSnapshotInsightsTrend;
        getInsights = parsed['getInsights'];
        getQnA = parsed['getQnA'];

        // Determine default view for historical snapshot: 1 brand -> Trend, Multi -> Normal
        if (AgentPayload && AgentPayload.promptData) {
          const pData = AgentPayload.promptData;
          const brands = pData.Primary_Brands || pData.primary_brands || pData.Brands || pData.brands || [];
          const combinedInsights = getSnapshotInsights || '';
          const hasTrendInsights = !!getSnapshotInsightsTrend || (typeof combinedInsights === 'string' && combinedInsights.includes('[[[SPLIT]]]'));

          if (hasTrendInsights && brands.length === 1) {
            comp.isTrendActive = true;
          } else {
            comp.isTrendActive = false;
          }
        }
        // Assign restored configuration to service for analytical controls
        if (getSnapshotConfiguration) {
          comp._configService.outputSnapshotData = getSnapshotConfiguration;
          comp.snapshotConfiguration = getSnapshotConfiguration; // Pass directly to popup
          if (getSnapshotConfiguration.brandMetadata) {
            comp._configService.brandData = getSnapshotConfiguration.brandMetadata;
          }
          if (getSnapshotConfiguration.weights) {
            comp._configService.weights = getSnapshotConfiguration.weights;
          }
        } else {
          console.warn('[CHATBOT] No getSnapshotConfiguration found in parsed response.');
        }
        comp.snapshotinsights_standard_text = getSnapshotInsights || '';
        comp.snapshotinsights_trendline_text = getSnapshotInsightsTrend || '';
      }

      const needAccessCheck = !isChatResponse && (!comp.validatedSessionId || comp.validatedSessionId !== comp.guidChatId);

      // Helper to identify available analytical views from orchestration response
      const identifyAvailableViews = (rawData: any, parsedResponses?: any) => comp.identifyAvailableViews(rawData, parsedResponses);


      const accessCheckRequest$ = needAccessCheck ? from(comp.getBearerToken()).pipe(
        switchMap(token => {
          return comp.apiService.accessCheck(
            comp.selected_client,
            comp.selected_study,
            token,
            comp.guidChatId || ''
          );
        }),
        tap((res: any) => {
          if (res && res.session_id) {
            comp.guidChatId = res.session_id;
            comp.modulesAccess = res.modules_access;
            comp.isCacheGenerated = false;
          }
          comp.validatedSessionId = comp.guidChatId;
        }),
        catchError((error) => {
          console.error('[ACCESS-CHECK ERROR] Access check failed:', error);
          return throwError(() => error);
        })
      ) : of(null);

      const generateCacheRequest$ = accessCheckRequest$.pipe(
        switchMap(() => {
          if (!comp.isCacheGenerated && comp.modulesAccess) {
            const activeModules: any = {};
            for (const [key, value] of Object.entries(comp.modulesAccess)) {
              if ((value as any).has_access) {
                activeModules[key] = value;
              }
            }

            return from(comp.getBearerToken()).pipe(
              switchMap(token => forkJoin([
                comp.apiService.generateRestrictedCache(
                  comp.selected_client,
                  comp.selected_study,
                  token,
                  comp.guidChatId,
                  activeModules
                ),
                comp.apiService.generateClientStudyContext(
                  comp.selected_client,
                  comp.selected_study,
                  token,
                  comp.guidChatId,
                  activeModules
                )
              ])),
              tap(() => {
                comp.isCacheGenerated = true;
              }),
              catchError(err => {
                console.error('[CACHE ERROR] Cache/Context generation failed:', err);
                return of(null);
              })
            );
          }
          return of(null);
        })
      );

      const orchestrationRequest$ = AgentPayload && isChatResponse
        ? of(AgentPayload)
        : generateCacheRequest$.pipe(
          switchMap(() => {
            return comp.getOrchestrationRequest();
          }),
          catchError((error) => {
            console.error('[ORCHESTRATION PIPE ERROR] Error in orchestration pipeline:', error);
            return throwError(() => error);
          })
        );

      if (AgentPayload && isChatResponse) {
      } else {
      }

      orchestrationRequest$.pipe(
        finalize(() => {
          comp.scrollSpacerHeight = 0; // Absolute guarantee to clean up spacer on stream termination/error!
          if (isChatResponse) {
            comp._loaderService.hideLoader();
          }
        })
      ).subscribe({
        next: async (response: any) => {
          let data = response;

          // Handle Snapshot Agent streaming events
          if (data && data.event) {
            if (data.event === 'final_result') {
              const rawData = data.data;
              let extractedMcode = rawData?.priority_mcode;
              if (extractedMcode === undefined || extractedMcode === null) {
                extractedMcode = data.mcode;
              }
              if (extractedMcode === undefined || extractedMcode === null) {
                extractedMcode = rawData.mcode || rawData.final_output?.mcode
                  || (rawData.member_responses ? (Object.values(rawData.member_responses)[0] as any)?.mcode : undefined);
              }
              if (extractedMcode === undefined || extractedMcode === null) {
                const firstChild = Object.values(rawData)[0] as any;
                extractedMcode = firstChild?.mcode || firstChild?.Mcode || 0;
              }
              let mcode = extractedMcode;
              comp.mcode = mcode;
              comp.isBrandAnalysis = (mcode === 2);

              // Store orchestration data for multi-view support
              comp.orchestrationRawData = rawData;
              const isDirectMemberResponses = !!(rawData.snapshot || rawData.brand_analysis || rawData.brandanalysis || rawData.crosstab);
              comp.allMemberResponses = rawData.member_responses || data.member_responses || (isDirectMemberResponses ? rawData : null);
              comp.availableViews = identifyAvailableViews(rawData, comp.allMemberResponses);

              // FAILSAFE: If identifyAvailableViews returned empty but we have allMemberResponses, build views directly
              if (comp.availableViews.length === 0 && comp.allMemberResponses) {
                console.warn('[MULTI-VIEW] identifyAvailableViews returned empty — building views directly from allMemberResponses keys:', Object.keys(comp.allMemberResponses));
                const mr = comp.allMemberResponses;
                const hasMrSnap = !!mr.snapshot;
                const hasMrBA = !!(mr.brand_analysis || mr.brandanalysis);
                const hasMrCT = !!mr.crosstab;
                if (hasMrSnap) comp.availableViews.push({ name: 'Snapshot', mcode: 1, key: 'snapshot' });
                if (hasMrBA) comp.availableViews.push({ name: 'Brand Analysis', mcode: 2, key: mr.brand_analysis ? 'brand_analysis' : 'brandanalysis' });
                if (hasMrCT) comp.availableViews.push({ name: 'Crosstab', mcode: 3, key: 'crosstab' });
              }

              // ULTIMATE FAILSAFE: If still no views (single-module path, no member_responses), create one view based on mcode
              if (comp.availableViews.length === 0) {
                const viewName = mcode === 1 ? 'Snapshot' : (mcode === 2 ? 'Brand Analysis' : 'Crosstab');
                const viewKey = mcode === 1 ? 'snapshot' : (mcode === 2 ? 'brand_analysis' : 'crosstab');
                comp.availableViews = [{ name: viewName, mcode: mcode, key: viewKey }];
                console.warn('[MULTI-VIEW] Ultimate fallback — created single view for mcode:', mcode);
              }

              // Fallback logic: If Team API returned mcode 99 (error) but we have multi-module access,
              // check if any specialized agent has a valid result in member_responses to use as fallback.
              const activeCount = (comp.hasSnapshotActive ? 1 : 0) + (comp.hasBrandAnalysisActive ? 1 : 0) + (comp.hasCrosstabActive ? 1 : 0);
              if (mcode === 99 && activeCount > 1 && comp.allMemberResponses) {
                if (comp.hasCrosstabActive && comp.allMemberResponses.crosstab) {
                  console.warn('[ORCHESTRATION] Team API returned 99. Falling back to Crosstab results.');
                  mcode = 3;
                  comp.mcode = 3;
                  comp.isBrandAnalysis = false;
                  comp.streamStatusText = 'Routing to Crosstab Analysis...';
                } else if (comp.hasBrandAnalysisActive && (comp.allMemberResponses.brand_analysis || comp.allMemberResponses.brandanalysis)) {
                  console.warn('[ORCHESTRATION] Team API returned 99. Falling back to Brand Analysis results.');
                  mcode = 2;
                  comp.mcode = 2;
                  comp.isBrandAnalysis = true;
                  comp.streamStatusText = 'Routing to Brand Analysis...';
                }
              }

              // [FIX] Always strictly sync activeView to match the final mcode.
              // availableViews.find(v => v.mcode === mcode) is authoritative.
              // If not found (module not in available views), fall back to first available and sync mcode.
              const newActiveView = comp.availableViews.find((v: any) => v.mcode === mcode) || comp.availableViews[0];
              if (newActiveView && newActiveView.mcode !== mcode) {
                console.warn(`[MULTI-VIEW] mcode ${mcode} not found in available views. Syncing mcode to: ${newActiveView.mcode}`);
                mcode = newActiveView.mcode;
                comp.mcode = mcode;
                comp.isBrandAnalysis = (mcode === 2);
              }

              // Always update activeView to match final mcode — don't skip based on previous state
              comp.activeView = newActiveView;
              if (targetBlock) {
                targetBlock.availableViews = [...comp.availableViews];
                targetBlock.activeView = comp.activeView;
                targetBlock.isBrandAnalysis = comp.isBrandAnalysis;
              }

              // Priority extraction for Step 2 Payload: final_output -> member_responses -> result
              let result = rawData.final_output;
              if (!result || Object.keys(result).length === 0 || mcode !== rawData.final_output?.mcode) {
                if (comp.allMemberResponses) {
                  // Select member response based on current (possibly fallback) mcode
                  if (mcode === 1) result = comp.allMemberResponses.snapshot;
                  else if (mcode === 2) result = comp.allMemberResponses.brand_analysis || comp.allMemberResponses.brandanalysis;
                  else if (mcode === 3) result = comp.allMemberResponses.crosstab;

                  // Absolute fallback to first available agent response if still empty
                  if (!result) result = Object.values(comp.allMemberResponses)[0];
                }
              }

              if (!result || Object.keys(result).length === 0) {
                result = rawData.result || {};
              }
              comp.chatPayload = comp.constructSpecialized(mcode, result, rawData);

              // Construct Parallel Payload for Background Step 2 (if multi-module)
              comp.parallelChatPayload = null;
              if (activeCount > 1 && rawData.member_responses) {
                // Secondary fallback: Crosstab > Brand Analysis
                if (comp.hasCrosstabActive && mcode !== 3 && rawData.member_responses.crosstab) {
                  comp.parallelChatPayload = comp.constructSpecialized(3, rawData.member_responses.crosstab, rawData);
                }
              }

              // Update UI state with the result data
              comp.promptData = result;
              comp.isTotalBase = result.isTotalBase ?? false;
              comp.selectedWeight = result.weight || result.Weight || comp.selectedWeight;

              if (comp.WeightDropDownList && comp.WeightDropDownList.length > 0) {
                const matchedWeight = comp.WeightDropDownList.find((w: any) => w.responseCode === comp.selectedWeight);
                comp.DefaultWeight = matchedWeight ? matchedWeight.Label : (comp.selectedWeight === 'Unweighted' ? 'Total Population' : comp.selectedWeight);
              } else {
                comp.DefaultWeight = comp.selectedWeight === 'Unweighted' ? 'Total Population' : comp.selectedWeight;
              }
              if (targetBlock) {
                targetBlock.selectedWeight = comp.selectedWeight;
                targetBlock.DefaultWeight = comp.DefaultWeight;
              }

              if (mcode === 1) {
                comp.widget_Id = result.Widget_ID || result.widget_id || comp.widget_Id;
                comp.widget_Name = result.Widget_Name || result.widget_name || comp.widget_Name;
              }

              // Normalize data structure for downstream dependencies
              const { timeline, total_elapsed_seconds, ...rest } = rawData;
              const originalPromptData = data.promptData;
              data = {
                ...rest,
                ...result,
                response: result || {}
              };
            } else if (['done', 'meta'].includes(data.event)) {
              return; // Skip system events
            } else {
              // Intermediate stream events

              // Only display 'status' event messages in the UI loader
              if (data.event === 'status') {
                // Support both structured {message: '...'} and plain string messages
                const statusMsg = data.message || data.data?.message || (typeof data.data === 'string' ? data.data : '');
                if (statusMsg && statusMsg.trim() !== '') {
                  const displayText = statusMsg.trim();
                  comp.queueStatusMessage(displayText);
                }
              }

              return; // Wait for final_result before processing core UI logic
            }
          }

          // Mark stream as finished but let the queue drain naturally to show sub-agent steps
          comp.isStreamFinished = true;


          const effectivePayload = isChatResponse ? data : comp.chatPayload;
          // CRITICAL: For fresh queries, comp.mcode is already authoritatively set in the final_result block above.
          // Only restore mcode from effectivePayload when replaying history (isChatResponse=true).
          // Overwriting comp.mcode from chatPayload.mcode here would undo the fallback-correct mcode.
          if (isChatResponse) {
            let restoredMcode = parsedHistory?.member_responses?.final_mcode || effectivePayload?.mcode || comp.mcode;

            // Smart fallback if mcode couldn't be determined from standard paths (especially single-module Snapshot)
            if (!restoredMcode || restoredMcode === 0) {
              if (parsedHistory?.getSnapshotInsights || parsedHistory?.getSnapshotOutput || parsedHistory?.getSnapshotConfiguration) {
                restoredMcode = 1;
              } else if (parsedHistory?.getConfiguration) {
                restoredMcode = parsedHistory?.GetMcode || 3;
              }
            }

            comp.mcode = restoredMcode;
            if (targetBlock) {
              targetBlock.mcode = comp.mcode;
              targetBlock.isBrandAnalysis = (comp.mcode === 2);
            }
          } else if (effectivePayload) {
            effectivePayload.mcode = comp.mcode;
          }
          // Also keep isBrandAnalysis in sync after potential mcode restore from history
          comp.isBrandAnalysis = (comp.mcode === 2);

          if (isChatResponse) {

            // Restore Step 2 data from history
            const restoredResponse = (comp.mcode === 1) ? getSnapshotOutput : getConfiguration;
            if (restoredResponse) {
              // Flatten the saved data to match fresh query structure
              data.response = restoredResponse.response ? restoredResponse.response : restoredResponse;
              if (restoredResponse.customization_Properties) {
                data.customization_Properties = restoredResponse.customization_Properties;
              }
              if (restoredResponse.quadrantText) {
                data.quadrantText = restoredResponse.quadrantText;
              }

              if (comp.mcode == 3 && restoredResponse.crosstab_payload) {
                comp.lastCrosstabPayload = restoredResponse.crosstab_payload;
              } else if (comp.mcode == 1) {
                comp.lastSnapshotPayload = restoredResponse.snapshot_payload || restoredResponse;
              } else if (comp.mcode == 2) {
                // Backend returns 'brand_anlysis_payload' (typo) — handle both spellings + effectivePayload fallback
                comp.lastBrandAnalysisPayload = restoredResponse.brand_analysis_payload || restoredResponse.brand_anlysis_payload || effectivePayload;
              }
            }

            // Restore UI context from history (Significance, Weight etc, and Error flags)
            if (parsedHistory) {
              if (parsedHistory['SignificanceVsReference']) comp.SignificanceVsReference = parsedHistory['SignificanceVsReference'];
              if (parsedHistory['DefaultConfidenceLevel']) comp.DefaultConfidenceLevel = parsedHistory['DefaultConfidenceLevel'];
              if (parsedHistory['selectedWeight']) comp.selectedWeight = parsedHistory['selectedWeight'];
              if (parsedHistory['failedCase'] !== undefined) {
                comp.failedCase = parsedHistory['failedCase'];
                if (targetBlock) targetBlock.failedCase = parsedHistory['failedCase'];
              }
              if (parsedHistory['noContentFlag'] !== undefined) comp.noContentFlag = parsedHistory['noContentFlag'];
              if (parsedHistory['errorHeader'] !== undefined) comp.errorHeader = parsedHistory['errorHeader'];
              if (parsedHistory['is_indexing'] !== undefined) comp.is_indexing = parsedHistory['is_indexing'];
              if (parsedHistory['is_sigtesting'] !== undefined) comp.is_sigtesting = parsedHistory['is_sigtesting'];
              if (parsedHistory['member_responses']) {
                comp.allMemberResponses = parsedHistory['member_responses'];
                comp.availableViews = comp.identifyAvailableViews(effectivePayload, comp.allMemberResponses);
                comp.activeView = comp.availableViews.find((v: any) => v.mcode === comp.mcode) || comp.availableViews[0];
                if (targetBlock) {
                  targetBlock.availableViews = [...comp.availableViews];
                  targetBlock.activeView = comp.activeView;
                }

                if (comp.mcode === 1) {
                  comp.showSnapshot = true;
                  comp.isBrandAnalysis = false;
                } else if (comp.mcode === 2) {
                  comp.showSnapshot = false;
                  comp.isBrandAnalysis = true;
                } else {
                  comp.showSnapshot = false;
                  comp.isBrandAnalysis = false;
                }
              }
            }

            // Ensure promptData is available for downstream dataObject reconstruction
            if (!data.promptData) {
              if (data === effectivePayload) {
                data.promptData = { ...effectivePayload };
                delete data.promptData.promptData;
              } else {
                data.promptData = effectivePayload;
              }
            }
          }

          let safePayload = effectivePayload || {};
          if (data === safePayload) {
            safePayload = { ...safePayload, ...(data.promptData || {}) };
            delete safePayload.promptData;
          }

          if (!isChatResponse) {
            comp.pushChatResponseEntry('AgentPayload', safePayload);
            comp.pushChatResponseEntry('SignificanceVsReference', comp.SignificanceVsReference);
            comp.pushChatResponseEntry('DefaultConfidenceLevel', comp.DefaultConfidenceLevel);
            comp.pushChatResponseEntry('selectedWeight', comp.selectedWeight);
            comp.pushChatResponseEntry('failedCase', comp.failedCase);
            comp.pushChatResponseEntry('noContentFlag', comp.noContentFlag);
            comp.pushChatResponseEntry('errorHeader', comp.errorHeader);
            comp.pushChatResponseEntry('is_indexing', comp.is_indexing);
            comp.pushChatResponseEntry('is_sigtesting', comp.is_sigtesting);
          }

          comp.promptData = safePayload;
          if (safePayload && !safePayload.mcode) {
            safePayload.mcode = comp.mcode;
          }
          if (comp.mcode === 1) {
            comp.snapshotConfiguration = comp.promptData;
          }


          // Requirement: Step 2 - Call unified endpoint with chatPayload to get finalized output data (Analytical Task)
          if (!isChatResponse && comp.chatPayload) {
            let opData: any;
            const activeCount = (comp.hasSnapshotActive ? 1 : 0) + (comp.hasBrandAnalysisActive ? 1 : 0) + (comp.hasCrosstabActive ? 1 : 0);

            try {
              // Step 2 logic for primary view
              const msgId: string = comp.selectedMessageId || (comp.chatPreviewBlocks.length > 0 ? (comp.chatPreviewBlocks[comp.chatPreviewBlocks.length - 1].messageId || 'current') : 'current');
              if (!comp.multiViewDataCache[msgId]) comp.multiViewDataCache[msgId] = {};

              // RACE CONDITION FIX: Wait for in-flight priority data if orchestration already started it
              if (!comp.multiViewDataCache[msgId][comp.mcode]) {
                let attempts = 0;
                while (attempts < 100 && !comp.multiViewDataCache[msgId][comp.mcode] && (comp.inFlightPreFetches[msgId]?.has(comp.mcode))) {
                  await new Promise(r => setTimeout(r, 100)); // Wait 100ms
                  attempts++;
                }
              }

              if (comp.multiViewDataCache[msgId]?.[comp.mcode]) {
                opData = comp.multiViewDataCache[msgId][comp.mcode];
              } else {
                // Only request if truly missing and not in-flight
                if (comp.mcode === 1) opData = await comp.apiService.snapshotPayloadAPI(comp.chatPayload).toPromise();
                else if (comp.mcode === 2) opData = await comp.apiService.brandAnalysisPayloadAPI(comp.chatPayload).toPromise();
                else if (comp.mcode === 3) opData = await comp.apiService.crosstabPayloadAPI(comp.chatPayload).toPromise();

                if (opData) comp.multiViewDataCache[msgId][comp.mcode] = opData;
              }

              // [DATA ISOLATION] Ensure ALL available views from orchestration have their Step 2 data fetched before saving history
              if (comp.availableViews && comp.availableViews.length > 1) {
                const fetchPromises = comp.availableViews.map(async (v: any) => {
                  if (v.mcode !== comp.mcode && !comp.multiViewDataCache[msgId]?.[v.mcode]) {
                    try {
                      const resData = comp.allMemberResponses?.[v.key] || comp.allMemberResponses?.[v.key?.replace('_', '')] || {};
                      const specialized = comp.constructSpecialized(v.mcode, resData);

                      const freshToken = await comp.getBearerToken();
                      if (freshToken) specialized.bearer_token = freshToken;

                      let vOpData = null;
                      if (v.mcode === 1) vOpData = await comp.apiService.snapshotPayloadAPI(specialized).toPromise();
                      else if (v.mcode === 2) vOpData = await comp.apiService.brandAnalysisPayloadAPI(specialized).toPromise();
                      else if (v.mcode === 3) vOpData = await comp.apiService.crosstabPayloadAPI(specialized).toPromise();

                      if (vOpData) {
                        const dataStr = vOpData?.response?.Data || vOpData?.Data;
                        const isEmpty = dataStr === '[]' || dataStr === '"{}"' || dataStr === '{}' || (Array.isArray(dataStr) && dataStr.length === 0);

                        if (isEmpty || !dataStr) {
                          console.warn(`[ORCHESTRATION] Missing data for pre-fetched view ${v.name}. Skipping insights.`);
                          return;
                        }

                        comp.multiViewDataCache[msgId][v.mcode] = vOpData;
                        await comp.fetchInsightsAsObservable(v.mcode, vOpData, msgId, specialized).toPromise();
                      }
                    } catch (err) {
                      console.warn(`[ORCHESTRATION] Failed to pre-fetch view ${v.name}`, err);
                    }
                  }
                });
                (comp as any).pendingBackgroundFetches = Promise.all(fetchPromises).then(() => {
                  // [DATA ISOLATION] Pre-package allMemberResponses synchronously before pushing it to chatResponse array
                  if (comp.allMemberResponses && comp.availableViews && comp.availableViews.length > 1) {
                    const safeStringify = (obj: any) => {
                      const cache = new WeakSet();
                      const ignoredKeys = ['Errors', 'Snapshot_Reason', 'BrandAnalysis_Reason', 'Crosstab_Reason'];
                      return JSON.stringify(obj, (key, value) => {
                        if (ignoredKeys.includes(key)) return undefined;
                        if (typeof value === 'object' && value !== null) {
                          if (cache.has(value)) return;
                          cache.add(value);
                        }
                        return value;
                      });
                    };

                    if (comp.allMemberResponses.brandanalysis && !comp.allMemberResponses.brand_analysis) {
                      comp.allMemberResponses.brand_analysis = comp.allMemberResponses.brandanalysis;
                      delete comp.allMemberResponses.brandanalysis;
                    }

                    const mods = [
                      { code: 1, key: 'snapshot', esKey: 'getSnapshotOutput' },
                      { code: 2, key: 'brand_analysis', esKey: 'getConfiguration' },
                      { code: 3, key: 'crosstab', esKey: 'getConfiguration' }
                    ];

                    mods.forEach(mod => {
                      if (comp.allMemberResponses && comp.allMemberResponses[mod.key]) {
                        const cachedOpData = comp.multiViewDataCache[msgId]?.[mod.code] || comp.multiViewDataCache['current']?.[mod.code];
                        if (cachedOpData) {
                          comp.allMemberResponses[mod.key][mod.esKey] = safeStringify(cachedOpData);
                        }
                        if (mod.code === 1 && comp.promptData) {
                          comp.allMemberResponses[mod.key]['getSnapshotConfiguration'] = typeof comp.promptData === 'string' ? comp.promptData : JSON.stringify(comp.promptData);
                        }
                        const insightsCache = comp.multiViewInsightsCache[msgId] || comp.multiViewInsightsCache['current'];
                        if (insightsCache && insightsCache[mod.code]) {
                          const cachedData = insightsCache[mod.code];
                          if (mod.code === 1) {
                            comp.allMemberResponses[mod.key]['getSnapshotInsights'] = cachedData.standard_insights || '';
                            comp.allMemberResponses[mod.key]['getSnapshotInsights_Trend'] = cachedData.trend_insights || '';
                          } else {
                            comp.allMemberResponses[mod.key]['getInsights'] = cachedData.rawInsights || '';
                          }
                          comp.allMemberResponses[mod.key]['getQnA'] = cachedData.qnaText || '';
                        }
                      }
                    });
                  }

                  if (comp.allMemberResponses && !isChatResponse) {
                    comp.pushChatResponseEntry('member_responses', comp.allMemberResponses);
                  }
                });
              } else {
                (comp as any).pendingBackgroundFetches = Promise.resolve();
              }

              if (opData) {
                data.response = opData.response || opData;
                data.payload = opData;

                if (comp.mcode == 3 && opData.crosstab_payload) {
                  comp.cleanBrandAnalysisChild(opData.crosstab_payload);
                  comp.lastCrosstabPayload = opData.crosstab_payload;
                } else if (comp.mcode == 1) {
                  comp.lastSnapshotPayload = opData.snapshot_payload || opData;
                } else if (comp.mcode == 2) {
                  // Backend returns 'brand_anlysis_payload' (typo) — handle both spellings + chatPayload fallback
                  const bPayload = opData.brand_analysis_payload || opData.brand_anlysis_payload || comp.chatPayload;
                  comp.cleanBrandAnalysisChild(bPayload);
                  comp.lastBrandAnalysisPayload = bPayload;
                }

                // History Bundling & Metadata persistence
                if (opData.response_payload_trendline) {
                  comp.currentRowPayloadTrendline = opData.response_payload_trendline;
                  data.response.response_payload_trendline = opData.response_payload_trendline;
                }
                if (opData.trendline_response) {
                  comp.currentTrendlineResponse = opData.trendline_response;
                  data.response.trendline_response = opData.trendline_response;
                }
                if (opData.customization_Properties_trendline) {
                  comp.currentCustomizationTrendline = opData.customization_Properties_trendline;
                  data.response.customization_Properties_trendline = opData.customization_Properties_trendline;
                }
                // Store response_payload for significance re-render support
                if (opData.response_payload) {
                  data.response.response_payload = opData.response_payload;
                }
                if (opData.customization_Properties) data.customization_Properties = opData.customization_Properties;
                if (opData.quadrantText) data.quadrantText = opData.quadrantText;
                data.promptData = comp.promptData;

                if (!isChatResponse) {
                  const key = (comp.mcode === 1) ? 'getSnapshotOutput' : 'getConfiguration';
                  const viewKey = comp.mcode === 1 ? 'snapshot' : (comp.mcode === 2 ? 'brand_analysis' : 'crosstab');
                  const esKey = comp.mcode === 1 ? 'getSnapshotOutput' : 'getConfiguration';

                  if (!comp.allMemberResponses) {
                    comp.allMemberResponses = {};
                  }

                  if (!comp.allMemberResponses[viewKey]) {
                    comp.allMemberResponses[viewKey] = {};
                  }

                  comp.allMemberResponses[viewKey][esKey] = JSON.stringify(opData);
                  if (comp.mcode === 1 && comp.promptData) {
                    comp.allMemberResponses[viewKey]['getSnapshotConfiguration'] = typeof comp.promptData === 'string' ? comp.promptData : JSON.stringify(comp.promptData);
                  }
                  comp.allMemberResponses.final_mcode = comp.mcode;

                  const cachedIns = (comp.multiViewInsightsCache[msgId] || {})[comp.mcode];
                  if (cachedIns) {
                    if (comp.mcode === 1) {
                      comp.allMemberResponses[viewKey]['getSnapshotInsights'] = cachedIns.standard_insights || '';
                      comp.allMemberResponses[viewKey]['getSnapshotInsights_Trend'] = cachedIns.trend_insights || '';
                    } else {
                      comp.allMemberResponses[viewKey]['getInsights'] = cachedIns.rawInsights || '';
                    }
                    if (cachedIns.qnaText) comp.allMemberResponses[viewKey]['getQnA'] = cachedIns.qnaText;
                  }

                  comp.pushChatResponseEntry(key, opData);
                  comp.pushChatResponseEntry('getConfiguration', opData);
                  comp.pushChatResponseEntry('getSnapshotConfiguration', comp.promptData);

                  if (cachedIns) {
                    if (comp.mcode === 1) {
                      comp.pushChatResponseEntry('getSnapshotInsights', cachedIns.standard_insights);
                      comp.pushChatResponseEntry('getSnapshotInsights_Trend', cachedIns.trend_insights);
                    } else {
                      comp.pushChatResponseEntry('getInsights', cachedIns.rawInsights);
                    }
                    if (cachedIns.qnaText) comp.pushChatResponseEntry('getQnA', cachedIns.qnaText);
                  }
                  comp.pushChatResponseEntry('member_responses', comp.allMemberResponses);

                  if ((comp.mcode === 1 || comp.mcode === 2 || comp.mcode === 3) && !isChatResponse) {
                    // Note: Actual save happens after insights reveal (cached or fresh)
                  } else if (!isChatResponse && activeCount <= 1) {
                    comp.saveChatList(targetBlock ? targetBlock.question : (comp.inputValue || comp.promptData?.user_input || 'Analytics Question'), comp.chatResponse, comp.defaultQuestionAnswer, targetBlock?.messageId);
                  }
                }
              }
            } catch (err: any) {
              console.error(`[API-ERROR] Failed to finalize analysis for mcode ${comp.mcode}:`, err);
              comp.isStreamFinished = false;
              comp.isInsightsStreaming = false;
              comp.stopAiLoader();
              comp.setFailedCaseState(true);

              let errorMsg = 'We encountered an unexpected error while finalizing your analysis. Please try again later.';
              comp.errorHeader = 'Analysis Error:';

              if (err.status === 400) {
                errorMsg = 'There was an issue with the request parameters. Please try refining your query.';
                comp.errorHeader = 'Invalid Request:';
              } else if (err.status === 500) {
                errorMsg = 'We\'re currently experiencing an internal server error while finalizing the analysis. Please try again later.';
                comp.errorHeader = 'Server Error:';
              }

              if (!isChatResponse) {
                comp.pushChatResponseEntry('failedCase', true);
                comp.pushChatResponseEntry('errorHeader', comp.errorHeader);
                if (comp.chatPreviewBlocks.length > 0) {
                  comp.updateQuestionAnswerForSelectedMessage('An error occurred during final processing.');
                }
                // Save failed state to history so it doesn't hang on reload
                comp.saveChatList(targetBlock ? targetBlock.question : (comp.inputValue || comp.currentQuestion || 'Analytics Question'), comp.chatResponse, errorMsg, targetBlock?.messageId);
              }

              comp.insightsVal = errorMsg;
              comp._insightsSerive.showInisghts({
                header: comp.errorHeader || 'Analysis Error',
                message: errorMsg,
                show: true,
              });
              comp.isInsightspopupOpen = true;
              return;
            }
          }



          if (!isChatResponse && (!data?.response || Object.keys(data.response).length === 0)) {
            const noContentMsg = 'I apologize, but I couldn\'t find any relevant data for your question. This might be because:<br><br>• The requested data is not available in the current study<br>• Your query might need to be more specific<br>• There may be data access restrictions<br><br>Please try rephrasing your question or contact your administrator for assistance.';
            const shortNoContentMsg = 'No relevant data found for the current study. Please rephrase your question.';

            comp.setFailedCaseState(true);
            comp.noContentFlag = true;
            comp.errorHeader = 'Data Status:';
            comp.isStreamFinished = false;
            comp.isInsightsStreaming = false;
            comp.arktabaccess = false;
            comp.isInsightspopupOpen = true;
            comp.insightsVal = noContentMsg;

            if (!isChatResponse) {
              comp.pushChatResponseEntry('failedCase', true);
              comp.pushChatResponseEntry('noContentFlag', true);
              comp.pushChatResponseEntry('errorHeader', comp.errorHeader);
            }

            if (targetBlock) {
              comp.selectedMessageId = targetBlock.messageId || null;
            } else if (comp.chatPreviewBlocks.length > 0) {
              comp.selectedMessageId = comp.chatPreviewBlocks[comp.chatPreviewBlocks.length - 1].messageId || null;
            }

            if (comp.chatPreviewBlocks.length > 0) {
              const lastIdx = comp.chatPreviewBlocks.length - 1;
              comp.updateQuestionAnswerForSelectedMessage(shortNoContentMsg);
              // Removed saveChatList to prevent recording 'no data' questions in history
              comp.isHistorySaving = false;
            } else if (comp.inputValue) {
              const tempMsgId = 'temp-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
              comp.selectedMessageId = tempMsgId;
              comp.chatPreviewBlocks.push({
                messageId: tempMsgId,
                question: comp.inputValue,
                response: '',
                QuestionAnswer: shortNoContentMsg,
                isTrendActive: comp.isTrendActive,
                isExportspopup: false,
                sampleSizeDropdown: false,
                confidenceLevelDropDown: false,
                referenceLevelDropDown: false,
                baseDropDown: false,
                weightDropDown: false,
                isReference: false,
                isSig: true,
                isIndex: false,
                DefaultSampleSize: 'None',
                SignificanceVsReference: '',
                DefaultConfidenceLevel: '95%',
                DefaultReferenceLevel: 'None',
                selectedWeight: comp.selectedWeight || 'Weight',
                DefaultWeight: comp.DefaultWeight || 'Total Population',
                DefaultBase: comp.DefaultBase || 'Total Base',
                isTotalBase: true,
                isAnswerBase: true,
                is_sigtesting: true,
                is_Time_Period_Present: true,
                isTranspose: false,
                dataDropDown: false,
                DefaultDataDropDown: 'Data',
                isTableClick: true,
                isVerticalStackChartClick: false,
                isVerticalBarChartClick: false,
                isVerticalGroupBarChartClick: false,
                isLineChartClick: false,
                isChartView: true,
                disableStackChart: false,
                disableLineChart: false,
                showStackTooltip: false,
                showLineTooltip: false,
                showBarTooltip: false,
                showMainTooltip: false,
                StackFlag: false,
                displayTable: true,
                isTableClosing: false,
                isChartClosing: false
              });
              // Removed saveChatList to prevent recording 'no data' questions in history
              comp.isHistorySaving = false;
            }

            setTimeout(() => {
              const chatScroll = document.querySelector('.chat-history-scroll');
              if (chatScroll) {
                chatScroll.scrollTop = chatScroll.scrollHeight;
              }
            }, 100);

            const noContentObj: InsightsModel = {
              header: comp.errorHeader,
              message: noContentMsg,
              show: true,
            };
            comp._insightsSerive.showInisghts(noContentObj);
            comp.isInsightspopupOpen = true;
            return;
          }
          const rowData = data.payload?.Row || data.promptData?.row || data.promptData?.Row;
          const colData = data.payload?.Column || data.promptData?.column || data.promptData?.Column;
          const colNestData = data.payload?.ColNesting || data.promptData?.col_nesting || data.promptData?.column_nesting || [];
          const rowNestData = data.payload?.RowNesting || data.promptData?.row_nesting || [];
          const filters = data.payload?.Filter || data.promptData?.filters || {};

          if (rowData && Array.isArray(rowData) && rowData.length > 0) {

            if (typeof rowData[0] === 'object' && rowData[0].Parent) {
              comp.AssignDataToDataObject(data.payload || data.promptData);
            } else {

              SessionStateHelper.syncDataObjectFromPrompt(comp, data.payload || data.promptData);
            }
          }


          if (comp.promptData && typeof comp.promptData === 'object') {
            comp.promptData.entryPointData = comp.dataArray;
            comp.isTotalBase = comp.promptData.isTotalBase !== undefined ? comp.promptData.isTotalBase : false;
            let fallbackSelected = comp.selectedWeight;
            if ((comp.mcode === 2 || comp.mcode === 3 || comp.promptData.mcode == 2 || comp.promptData.mcode == 3) && !comp.promptData.weight && !comp.promptData.Weight) {
                const primary = comp.WeightDropDownList?.find((w: any) => w.Label === 'Primary Weight' || w.responseCode?.toLowerCase() === 'weightb');
                if (primary) fallbackSelected = primary.responseCode;
            }
            comp.selectedWeight = comp.promptData.weight || comp.promptData.Weight || fallbackSelected;
            comp.isAnswerBase = !comp.isTotalBase;
            comp.DefaultBase = comp.isTotalBase ? 'Total Base' : 'Answer Base';

            if (comp.WeightDropDownList && comp.WeightDropDownList.length > 0) {
              const matchedWeight = comp.WeightDropDownList.find((w: any) => w.responseCode === comp.selectedWeight);
              if (matchedWeight) {
                if (matchedWeight.responseCode?.toLowerCase() === 'weightb') comp.DefaultWeight = 'Primary Weight';
                else if (matchedWeight.responseCode?.toLowerCase() === 'weight') comp.DefaultWeight = 'Secondary Weight';
                else if (matchedWeight.responseCode?.toLowerCase() === 'unweighted') comp.DefaultWeight = 'Unweighted';
                else comp.DefaultWeight = matchedWeight.Label;
              } else {
                comp.DefaultWeight = (comp.selectedWeight === 'Unweighted' || comp.selectedWeight === 'unweighted') ? 'Unweighted' : comp.selectedWeight;
              }
            } else {
              comp.DefaultWeight = (comp.selectedWeight === 'Unweighted' || comp.selectedWeight === 'unweighted') ? 'Unweighted' : comp.selectedWeight;
            }

            if (!isChatResponse && comp.chatPreviewBlocks.length === 0 && comp.promptData?.user_input) {
              comp.chatPreviewBlocks.push({
                question: comp.promptData.user_input,
                response: '',
                QuestionAnswer: comp.defaultQuestionAnswer,
                isTrendActive: comp.isTrendActive,
                isExportspopup: false,
                sampleSizeDropdown: false,
                confidenceLevelDropDown: false,
                referenceLevelDropDown: false,
                baseDropDown: false,
                weightDropDown: false,
                isReference: false,
                isSig: true,
                isIndex: false,
                DefaultSampleSize: 'None',
                SignificanceVsReference: '',
                DefaultConfidenceLevel: '95%',
                DefaultReferenceLevel: 'None',
                selectedWeight: comp.selectedWeight || 'Weight',
                DefaultWeight: comp.DefaultWeight || 'Total Population',
                DefaultBase: comp.DefaultBase || 'Total Base',
                isTotalBase: comp.isTotalBase,
                isAnswerBase: comp.isAnswerBase,
                is_sigtesting: true,
                is_Time_Period_Present: true,
                isTranspose: false,
                dataDropDown: false,
                DefaultDataDropDown: 'Data',
                isTableClick: true,
                isVerticalStackChartClick: false,
                isVerticalBarChartClick: false,
                isVerticalGroupBarChartClick: false,
                isLineChartClick: false,
                isChartView: true,
                disableStackChart: false,
                disableLineChart: false,
                showStackTooltip: false,
                showLineTooltip: false,
                showBarTooltip: false,
                showMainTooltip: false,
                StackFlag: false,
                displayTable: true,
                isTableClosing: false,
                isChartClosing: false
              });
            }
          } else {
            comp.isTotalBase = false;
            let defaultFallback = null;
            if (comp.mcode === 2 || comp.mcode === 3) {
              defaultFallback = comp.WeightDropDownList?.find((w: any) => w.Label === 'Secondary Weight' || w.responseCode?.toLowerCase() === 'weight');
            }
            if (!defaultFallback) {
              defaultFallback = comp.WeightDropDownList?.find((w: any) => w.Label === 'Primary Weight' || w.responseCode?.toLowerCase() === 'weightb');
            }
            if (defaultFallback) {
              comp.selectedWeight = defaultFallback.responseCode;
              comp.DefaultWeight = defaultFallback.Label;
            } else {
              comp.selectedWeight = 'Unweighted';
              if (comp.WeightDropDownList && comp.WeightDropDownList.length > 0) {
                const matchedWeight = comp.WeightDropDownList.find((w: any) => w.responseCode === comp.selectedWeight);
                if (matchedWeight) {
                  if (matchedWeight.responseCode?.toLowerCase() === 'weightb') comp.DefaultWeight = 'Primary Weight';
                  else if (matchedWeight.responseCode?.toLowerCase() === 'weight') comp.DefaultWeight = 'Secondary Weight';
                  else if (matchedWeight.responseCode?.toLowerCase() === 'unweighted') comp.DefaultWeight = 'Unweighted';
                  else comp.DefaultWeight = matchedWeight.Label;
                } else {
                  comp.DefaultWeight = 'Unweighted';
                }
              } else {
                comp.DefaultWeight = 'Unweighted';
              }
            }
          }

          const msgId = comp.selectedMessageId || 'current';

          // 1. GLOBAL INSIGHT SYNCHRONIZATION:
          // [REMOVED] The primary module no longer waits for background insights. 
          // It will immediately plot the UI and let callInsightsAPI stream the insights.

          // Requirement: Snapshot module (mcode==1) with Elasticsearch chat history support
          // Support both fresh API responses and saved chat data from Elasticsearch
          if (comp.mcode == 1) {
            comp._configService.snapshotProductType = 'custom';
            comp._configService.selectedSnapshotType = data.promptData?.Snapshot_Type || data.promptData?.snapshot_type;
            comp.selectedSection = data.promptData?.Section || data.promptData?.section;
            comp.widget_Id = data.promptData?.Widget_ID || data.promptData?.widget_id;
            comp.widget_Name = data.promptData?.Widget_Name || data.promptData?.widget_name;

            comp.widegtconfigdetails.section = comp.promptData?.Section || comp.promptData?.section || '';

            // UNCONDITIONALLY build the Selection Summary for Snapshot
            comp._configService.selectionSummaryTextTitle = SelectionSummaryHelper.buildSnapshotSelectionSummary(comp, data);
            comp.selectionSummary = comp._configService.selectionSummaryTextTitle;

            if (targetBlock) {
              targetBlock.selectionSummary = comp.selectionSummary;
            }

            // Select data source: getSnapshotOutput for saved chats, data.response for fresh queries
            let snapshotDataSource = null;
            if (isChatResponse && getSnapshotOutput) {
              snapshotDataSource = getSnapshotOutput;
              // Ensure trendline data is available for historical chats
              if (snapshotDataSource.response_payload_trendline) {
                comp.currentRowPayloadTrendline = snapshotDataSource.response_payload_trendline;
              }
              if (snapshotDataSource.trendline_response) {
                comp.currentTrendlineResponse = snapshotDataSource.trendline_response;
              }
              if (snapshotDataSource.customization_Properties_trendline) {
                comp.currentCustomizationTrendline = snapshotDataSource.customization_Properties_trendline;
              }
            } else if (data != null && data != undefined && data != '' && data.response != null && data.response.Data != null) {
              snapshotDataSource = data.response;
            } else {
            }

            if (snapshotDataSource != null && snapshotDataSource.Data != null) {
              try {
                let opData = JSON.parse(snapshotDataSource.Data);
                comp.MainResult = opData;


                // Requirement: Save snapshot output to Elasticsearch for chat history persistence
                if (!isChatResponse) {
                  comp.pushChatResponseEntry('getSnapshotOutput', snapshotDataSource);
                }

                const sortedOPdata = opData.sort(
                  (a: any, b: any) => a.widgetNumber - b.widgetNumber
                );

                const snapTypeVal = comp.promptData.Snapshot_Type || comp.promptData.snapshot_type;
                if (snapTypeVal) {
                  comp._configService.selectedcompareacrossOption = snapTypeVal;
                }

                // (Removed legacy try/catch SelectionSummary block that was meant for crosstabs)

                comp._configService.standardSnapOutputData = sortedOPdata;
                comp.OutputData = sortedOPdata;

                if (sortedOPdata && sortedOPdata.length > 0) {

                  // Requirement: Prepare filteredWidgetData structure for output-popup component
                  if (comp.widgetData && comp.widgetData.length > 0) {
                    const matchedWidget = comp.widgetData.find(
                      (widget: any) => widget.previewChartPostion == comp.promptData.Widget_ID
                    );

                    if (matchedWidget) {
                      matchedWidget.customizationProperties = comp.getFormattedCustomProperties(matchedWidget.customizationProperties, matchedWidget.widgetchart_name);

                      // Re-apply colChild formatting for multi-market in pre-structured widgetData
                      if (matchedWidget.metric?.[0]?.metric_value) {
                        matchedWidget.metric[0].metric_value.forEach((metricRow: any) => {
                          if (metricRow.data && Array.isArray(metricRow.data)) {
                            metricRow.data.forEach((dataArrEntry: any) => {
                              const colParts = (dataArrEntry.colChild || '').split('|');
                              const specificCol = colParts.pop() || dataArrEntry.colChild;
                              const parentLabel = colParts.pop() || 'Market';

                              // Use only market name as specific part (Table1 uses split('|')[1])
                              dataArrEntry.colChild = `${parentLabel}|${specificCol}`;
                            });
                          }
                        });
                      }

                      comp.filteredWidgetData = matchedWidget;
                      comp.filteredWidgetID = matchedWidget.previewChartPostion;
                    } else {

                      // SMART WIDGET SELECTION: Match question natively first, then query Widget_ID, then default
                      let selectedWidget: any = null;
                      const currentQuestion = (comp.currentQuestion || '').toLowerCase();
                      const uniqueWidgets = _.uniqBy(sortedOPdata, 'widgetNumber');

                      if (currentQuestion) {
                        const matchedWidget: any = uniqueWidgets.find((widget: any) =>
                          currentQuestion.includes(widget.widgetName.toLowerCase())
                        );
                        if (matchedWidget) {
                          selectedWidget = matchedWidget;
                        }
                      }

                      if (!selectedWidget) {
                        const targetWidgetID = comp.promptData?.Widget_ID || comp.promptData?.widget_id;
                        if (targetWidgetID) {
                          const idMatchedWidget: any = uniqueWidgets.find((widget: any) => String(widget.widgetNumber) === String(targetWidgetID));
                          if (idMatchedWidget) {
                            selectedWidget = idMatchedWidget;
                          }
                        }
                      }

                      if (!selectedWidget) {
                        selectedWidget = sortedOPdata[0];
                      }

                      // Filter sortedOPdata to only include rows from the selected widget
                      const widgetFilteredData = sortedOPdata.filter((item: any) =>
                        item.widgetNumber === selectedWidget.widgetNumber
                      );


                      // Fallback base for rows with null base (like 'Overall Consideration')
                      const fallbackBase = widgetFilteredData.find((r: any) => r.answerUnweightedBase != null)?.answerUnweightedBase || 999;
                      const uniqueMetrics = _.uniqBy(widgetFilteredData, 'rowChild').map((item: any) => ({
                        option: item.rowChild,
                        selectOrder: 0,
                        typeofmetric: 'METRIC',
                        metric_group: '',
                        data: widgetFilteredData
                          .filter((row: any) => row.rowChild === item.rowChild)
                          .map((row: any) => {
                            const dataValue = row.answerWeightedShare;
                            const data2Value = row.answerWeightedShareIndex;

                            // Specific Market extraction for Table1Component compatibility:
                            const colParts = (row.colChild || '').split('|');
                            const specificCol = colParts.pop() || row.colChild;
                            const parentLabel = colParts.pop() || 'Market';

                            // Format: "Parent|Market" (Table1 uses split('|')[1])
                            const resolvedColChild = `${parentLabel}|${specificCol}`;

                            return {
                              data: dataValue,
                              data2: data2Value,
                              lvl1Child: row.Level1ColChild,
                              colChild: resolvedColChild,
                              rowChild: row.rowChild,
                              totalUnweightedBase: row.totalUnweightedBase,
                              totalUnweightedShare: row.totalUnweightedShare,
                              totalWeightedBase: row.totalWeightedBase,
                              totalWeightedShare: row.totalWeightedShare,
                              totalWeightedShareIndex: row.totalWeightedShareIndex,
                              answerUnweightedBase: row.answerUnweightedBase,
                              answerUnweightedShare: row.answerUnweightedShare,
                              answerWeightedBase: row.answerWeightedBase,
                              answerWeightedShare: row.answerWeightedShare,
                              answerWeightedShareIndex: row.answerWeightedShareIndex,
                              changeVsPY_Answer: row.changeVsPY_Answer,
                              changeVsPY_Total: row.changeVsPY_Total,
                              Level1ColResponseCode: row.Level1ColResponseCode,
                              Level1NestColVariableCode: row.Level1NestColVariableCode,
                              CalculationType: row.CalculationType || 'share',
                              CalculationTypeIndex: row.CalculationTypeIndex || 'index',
                              baseValueforLSorIs: row.answerUnweightedBase ?? fallbackBase,
                              changeVsPY: row.changeVsPY_Answer,
                              Significance_Value: row.Significance_Answer
                            };
                          })
                      }));

                      // Sort uniqueMetrics in descending order of values
                      uniqueMetrics.sort((a: any, b: any) => {
                        const valA = a.data?.[0]?.data ?? 0;
                        const valB = b.data?.[0]?.data ?? 0;
                        return valB - valA; // Descending
                      });

                      const isBubbleChart = selectedWidget.widgetChartType.toLowerCase().includes('bubble');

                      comp.filteredWidgetData = {
                        widgetchart_name: selectedWidget.widgetChartType,
                        widget_name: selectedWidget.widgetName,
                        previewChartPostion: selectedWidget.widgetNumber,
                        customizationProperties: comp.getFormattedCustomProperties(selectedWidget.customization_Properties || data.customization_Properties, selectedWidget.widgetChartType),
                        quadrantText: data.quadrantText ? JSON.stringify(data.quadrantText) : null,
                        metric: isBubbleChart ? [
                          { metric_option: 'XAxis Metric', metric_group: '', metric_value: [uniqueMetrics[0]] },
                          { metric_option: 'YAxis Metric', metric_group: '', metric_value: [uniqueMetrics[1]] },
                          { metric_option: 'ZAxis Metric', metric_group: '', metric_value: [uniqueMetrics[2]] }
                        ] : [{
                          metric_option: 'Data',
                          metric_group: '',
                          metric_value: uniqueMetrics
                        }],
                        response_payload_trendline: data.response_payload_trendline,
                        trendline_response: data.trendline_response
                      };
                      comp.filteredWidgetID = selectedWidget.widgetNumber;

                    }
                  } else {

                    // SMART WIDGET SELECTION: Match question to widget name first, fallback to payload Widget_ID, then default to first
                    let selectedWidget: any = null;
                    const currentQuestion = (comp.currentQuestion || '').toLowerCase();
                    const uniqueWidgets = _.uniqBy(sortedOPdata, 'widgetNumber');


                    // 1. Try to find widget whose name matches the user's question
                    if (currentQuestion) {
                      const matchedWidget: any = uniqueWidgets.find((widget: any) =>
                        currentQuestion.includes(widget.widgetName.toLowerCase())
                      );

                      if (matchedWidget) {
                        selectedWidget = matchedWidget;
                      }
                    }

                    // 2. If no text match, fallback to what the Python orchestrator provided
                    if (!selectedWidget) {
                      const targetWidgetID = comp.promptData?.Widget_ID || comp.promptData?.widget_id;
                      if (targetWidgetID) {
                        const idMatchedWidget: any = uniqueWidgets.find((widget: any) => String(widget.widgetNumber) === String(targetWidgetID));
                        if (idMatchedWidget) {
                          selectedWidget = idMatchedWidget;
                        }
                      }
                    }

                    // 3. Absolute fallback to first index
                    if (!selectedWidget) {
                      selectedWidget = sortedOPdata[0];
                    }

                    // Filter sortedOPdata to only items from the selected widget
                    const widgetFilteredData = sortedOPdata.filter((item: any) =>
                      item.widgetNumber === selectedWidget.widgetNumber
                    );


                    // Fallback base for rows with null base (like 'Overall Consideration')
                    const fallbackBase = widgetFilteredData.find((r: any) => r.answerUnweightedBase != null)?.answerUnweightedBase || 999;
                    const uniqueMetrics = _.uniqBy(widgetFilteredData, 'rowChild').map((item: any) => ({
                      option: item.rowChild,
                      selectOrder: 0,
                      typeofmetric: 'METRIC',
                      metric_group: '',
                      data: widgetFilteredData
                        .filter((row: any) => row.rowChild === item.rowChild)
                        .map((row: any) => {
                          const dataValue = row.answerWeightedShare;
                          const data2Value = row.answerWeightedShareIndex;

                          // Specific Market extraction for Table1Component compatibility:
                          const colParts = (row.colChild || '').split('|');
                          const specificCol = colParts.pop() || row.colChild;
                          const parentLabel = colParts.pop() || 'Market';

                          // Format: "Parent|Market" (Table1 uses split('|')[1])
                          const resolvedColChild = `${parentLabel}|${specificCol}`;

                          return {
                            data: dataValue,
                            data2: data2Value,
                            lvl1Child: row.Level1ColChild,
                            colChild: resolvedColChild,
                            rowChild: row.rowChild,
                            totalUnweightedBase: row.totalUnweightedBase,
                            totalUnweightedShare: row.totalUnweightedShare,
                            totalWeightedBase: row.totalWeightedBase,
                            totalWeightedShare: row.totalWeightedShare,
                            totalWeightedShareIndex: row.totalWeightedShareIndex,
                            answerUnweightedBase: row.answerUnweightedBase,
                            answerUnweightedShare: row.answerUnweightedShare,
                            answerWeightedBase: row.answerWeightedBase,
                            answerWeightedShare: row.answerWeightedShare,
                            answerWeightedShareIndex: row.answerWeightedShareIndex,
                            changeVsPY_Answer: row.changeVsPY_Answer,
                            changeVsPY_Total: row.changeVsPY_Total,
                            Level1ColResponseCode: row.Level1ColResponseCode,
                            Level1NestColVariableCode: row.Level1NestColVariableCode,
                            CalculationType: row.CalculationType || 'share',
                            CalculationTypeIndex: row.CalculationTypeIndex || 'index',
                            baseValueforLSorIs: row.answerUnweightedBase ?? fallbackBase,
                            changeVsPY: row.changeVsPY_Answer,
                            Significance_Value: row.Significance_Answer
                          };
                        })
                    }));

                    // Sort uniqueMetrics in descending order of values as per user request
                    uniqueMetrics.sort((a: any, b: any) => {
                      const valA = a.data?.[0]?.data ?? 0;
                      const valB = b.data?.[0]?.data ?? 0;
                      return valB - valA; // Descending
                    });

                    const isBubbleChart = selectedWidget.widgetChartType.toLowerCase().includes('bubble');

                    comp.filteredWidgetData = {
                      widgetchart_name: selectedWidget.widgetChartType,
                      widget_name: selectedWidget.widgetName,
                      previewChartPostion: selectedWidget.widgetNumber,
                      customizationProperties: comp.getFormattedCustomProperties(data.customization_Properties || selectedWidget.customization_Properties, selectedWidget.widgetChartType),
                      quadrantText: data.quadrantText ? JSON.stringify(data.quadrantText) : null,
                      metric: isBubbleChart ? [
                        { metric_option: 'XAxis Metric', metric_group: '', metric_value: [uniqueMetrics[0]] },
                        { metric_option: 'YAxis Metric', metric_group: '', metric_value: [uniqueMetrics[1]] },
                        { metric_option: 'ZAxis Metric', metric_group: '', metric_value: [uniqueMetrics[2]] }
                      ] : [{
                        metric_option: 'Data',
                        metric_group: '',
                        metric_value: uniqueMetrics  // Array of objects, not JSON string
                      }],
                      response_payload_trendline: data.response_payload_trendline,
                      trendline_response: data.trendline_response
                    };
                    comp.filteredWidgetID = selectedWidget.widgetNumber;


                    const compareAcross = data.promptData?.Snapshot_Type || data.promptData?.snapshot_type;
                    const section = data.promptData?.Section || data.promptData?.section;

                    const pData = data.promptData || {};
                    const marketsArray = pData.Markets || pData.markets || pData.Market || pData.market || [];
                    const brandsArray = pData.Primary_Brands || pData.primary_brands || pData.Brands || pData.brands || [];
                    const timeperiodArray = pData.Time_Period || pData.time_period || pData.TimePeriod || pData.timeperiod || [];
                    const metricsArray = pData.Primary_Metrics || pData.primary_metrics || pData.Metrics || pData.metrics || pData.Selected_Metrics || [];
                    const filtersArray = pData.Filter_Audience || pData.filter_audience || pData.Filters || pData.filters || [];


                    const extractValue = (item: any) => {
                      if (typeof item === 'string') {
                        const parts = item.split('|');
                        if (parts.length >= 2) {
                          return parts[parts.length - 2] + ': ' + parts[parts.length - 1];
                        }
                        return parts.pop();
                      }
                      if (item && item.name) return item.name;
                      if (item && item.Child) return item.Child;
                      return String(item);
                    };

                    const extractLastPartOnly = (item: any) => {
                      if (typeof item === 'string') return item.split('|').pop();
                      if (item && item.name) return item.name;
                      if (item && item.Child) return item.Child;
                      return String(item);
                    };

                    const markets = marketsArray.length > 0 ? marketsArray.map((f: any) => extractLastPartOnly(f)).join(', ') : '';
                    const entryPoint = 'Markets: ' + markets;

                    const brands = brandsArray.length > 0 ? brandsArray.map((c: any) => extractValue(c)).join(', ') : '';
                    const timeperiod = timeperiodArray.length > 0 ? timeperiodArray.map((f: any) => extractValue(f)).join(', ') : '';
                    const filters = filtersArray.length > 0 ? filtersArray.map((f: any) => extractValue(f)).join(', ') : 'All Respondents';

                    const primaryMetric = metricsArray.map((r: any) => extractValue(r)).join(', ');
                    const metricNames = primaryMetric || uniqueMetrics.map((m: any) => m.option).join(', ');

                    const baseType = data.promptData?.isTotalBase ? BASE_TYPES.TOTAL : BASE_TYPES.ANSWER;


                    let selections = 'Time Period: ' + timeperiod + '; Brands: ' + brands;
                    if (filtersArray.length > 0) {
                      selections += '; Filters: ' + filters;
                    }

                    comp._configService.selectionSummaryTextTitle =
                      'Compare Across: ' + compareAcross + ': ' + section +
                      ' || Entry Point: Markets: ' + markets +
                      ' || Selections: ' + selections +
                      ' || Significance: None' +
                      ' || Base: ' + baseType +
                      ' || Weight: Primary Weight';

                    if (targetBlock) {
                      targetBlock.selectionSummary = comp._configService.selectionSummaryTextTitle;
                    }

                    comp.GenerateSelectionSummary();

                    if (!comp.Snapshotdataforexport) {
                      comp.Snapshotdataforexport = comp._configService.getDropdownValues();
                    }

                    comp.Snapshotdataforexport.compareAcross = compareAcross;
                    if (marketsArray.length > 1) {
                      comp._configService.selectedSnapshotType = 'Multiple Markets';
                    } else {
                      comp._configService.selectedSnapshotType = compareAcross || 'Standard Chart';
                    }
                    comp.Snapshotdataforexport.snapshotName = data.promptData?.Snapshot_Name || 'Snapshot';
                    comp.Snapshotdataforexport.clientName = comp.selected_client;
                    comp.Snapshotdataforexport.studyName = comp.selected_study;
                    comp.Snapshotdataforexport.selectionSummary = comp._configService.selectionSummaryTextTitle;
                    comp.Snapshotdataforexport.baseType = baseType;
                  }
                } else {
                }


                if (comp.widgetData && comp.widgetData.length > 0) {
                  comp.afterSubmit();
                } else {
                  comp.outputData = sortedOPdata;

                  // Populate allPageOutputData / allPageOutputDataWithoutTrend so
                  // editablePPTClicked() and excelexportClicked() can find the data
                  // even when afterSubmit() was not called.
                  if (comp.filteredWidgetData) {
                    const widgetItems = [comp.filteredWidgetData];
                    comp._configService.allPageOutputData = [widgetItems];
                    comp._configService.allPageOutputDataWithoutTrend = [widgetItems];
                  }
                }

                const outputViewDiv = document.querySelector('.output-view');
                if (outputViewDiv) {
                  outputViewDiv?.classList.remove('DN');
                }


                // [UNIVERSAL TREND INJECTION] 
                // Hard-inject stored trend components into filteredWidgetData just before exposing to UI
                if (comp.filteredWidgetData) {
                  if (comp.currentRowPayloadTrendline) comp.filteredWidgetData.response_payload_trendline = comp.currentRowPayloadTrendline;
                  if (comp.currentTrendlineResponse) comp.filteredWidgetData.trendline_response = comp.currentTrendlineResponse;
                  if (comp.currentCustomizationTrendline) comp.filteredWidgetData.customization_Properties_trendline = comp.currentCustomizationTrendline;
                  // Inject response_payload so the popup can call snapshotPayloadService on significance change
                  const storedResponsePayload = data?.response?.response_payload;
                  if (storedResponsePayload && !comp.filteredWidgetData.response_payload) {
                    comp.filteredWidgetData.response_payload = storedResponsePayload;
                  }

                  // [METADATA INJECTION] Ensure the popup has context for multi-brand/multi-market logic
                  const resolvedCompareAcross = data.promptData?.Snapshot_Type || data.promptData?.snapshot_type;
                  comp.filteredWidgetData.snapshot_type = resolvedCompareAcross || comp._configService.selectedSnapshotType || 'Single Brand';
                  comp.filteredWidgetData.selectedcompareacrossOption = resolvedCompareAcross || comp._configService.selectedcompareacrossOption;
                }

                comp.showSnapshot = true;

                setTimeout(() => MarkdownPayloadHelper.buildSnapshotMarkdown(comp), 500);
              } catch (error) {
              }
            } else {
            }

            comp.Snapshotdataforexport = comp._configService.getDropdownValues();

            if (!comp.selectionSummary || comp.selectionSummary === '') {
              comp.selectionSummary = 'Snapshot Analysis';
            }

            if (targetBlock) {
              targetBlock.selectionSummary = comp.selectionSummary;
            }
            if (!comp.confidenceLevel || comp.confidenceLevel === '') {
              comp.confidenceLevel = '90%';
            }
            if (!comp.selected_study) {
              comp.selected_study = comp.landingdata?.study || '';
            }
            if (!comp.selected_client) {
              comp.selected_client = comp.landingdata?.client_code || comp.promptData?.Client_Code;
            }

            const insightsStartTime = performance.now();

            // Call insights API for snapshot (mcode == 1)
            const markdownData = comp.buildSnapshotMarkdownForAPI();
            const widget_details = {
              snapshot_type: comp.promptData?.Snapshot_Type || comp.promptData?.snapshot_type || '',
              snapshot_name: comp.promptData?.Snapshot_Name || comp.promptData?.snapshot_name || '',
              section: comp.promptData?.Section || comp.promptData?.section || '',
              widget_id: comp.promptData?.Widget_ID || comp.promptData?.widget_id || '',
              widget_name: comp.promptData?.Widget_Name || comp.promptData?.widget_name || ''
            };

            if (isChatResponse) {
              if (getSnapshotInsights) {
                comp.OnInsigthsbuttonClick(isChatResponse, getSnapshotInsights, getSnapshotInsightsTrend, targetBlock);
              }
              if (getSnapshotLoadData) {
                comp.stopAiLoader();
                comp.arktabaccess = false;
              } else {
                if (!comp.insightsVal || comp.insightsVal.length === 0) {
                  comp.insightsVal = ['Insights not available for this historical chat.'];
                }
                comp.stopAiLoader();
                comp.arktabaccess = false;
              }
            } else {
              // Use cached insights if available (from pre-fetch)
              const cached = (comp.multiViewInsightsCache[msgId] || {})[comp.mcode];
              if (cached) {
                comp.insightsVal = cached.insightsVal;
                comp.updateMarkdownTextRealTime(cached.qnaText);
                comp.isInsightspopupOpen = true;
                comp.isInsightsStreaming = false;
                comp.isStreamFinished = true;
                comp.statusQueue = []; // Clear queue to instantly show post-stream message
                setTimeout(() => {
                  comp.stopAiLoader();
                  comp.arktabaccess = false;
                }, 1500);

                if (!isChatResponse) {
                  // Insights already pushed in Step 2 bundling above if cached
                  comp.saveChatList(
                    targetBlock ? targetBlock.question : (comp.chatPreviewBlocks.length > 0 ? comp.chatPreviewBlocks[comp.chatPreviewBlocks.length - 1].question : comp.inputValue),
                    comp.chatResponse,
                    cached.qnaText || comp.getFirstTwoInsightsAsPlainText() || 'Insights ready.',
                    targetBlock?.messageId
                  );
                }
              } else if (!comp.failedCase) {
                console.warn(`[ORCHESTRATION] No cached insights for mcode: ${comp.mcode}. Triggering fallback.`);

                // Unblock the main UI wrapper immediately by setting the selected message ID to the active block
                if (targetBlock && targetBlock.messageId) {
                  comp.selectedMessageId = targetBlock.messageId;
                }

                setTimeout(() => {
                  comp.callInsightsAPI(comp.mcode, markdownData, comp.currentQuestion, widget_details, targetBlock);
                }, 100);
              }
            }
          }

          if (comp.mcode != 1) {
            comp.LoadRibbonData(comp.filteredlist);

            // Force crosstab view for mcode 2 and 3
            comp.showSnapshot = false;
            comp.arktabaccess = false;
            comp.isDiagnostic = true;
            comp.existingquery = comp.inputValue;
            comp.OutputdataObject.Row = [];
            comp.OutputdataObject.Column = [];
            comp.CrossTabOutPutData = [];



            if (data.response != null && data.response.HasValidData !== false) {
              let outputstring = data.response;
              comp.displayTable = true;
              comp.resetChartStates('table');

              try {
                if (typeof outputstring.Data === 'string') {
                  comp.CrossTabOutPutData = JSON.parse(outputstring.Data);
                } else if (Array.isArray(outputstring.Data)) {
                  comp.CrossTabOutPutData = outputstring.Data;
                } else {
                  console.error('Unexpected outputstring.Data format:', typeof outputstring.Data);
                  comp.CrossTabOutPutData = [];
                }
              } catch (parseError) {
                console.error('Error parsing outputstring.Data:', parseError);
                console.error('Raw outputstring.Data:', outputstring.Data);
                comp.CrossTabOutPutData = [];
              }
              comp.SelectionSummary();
              if (targetBlock) {
                targetBlock.selectionSummary = comp.selectionSummary;
              }

              comp.variableTabData = comp.receivedData;
              if (typeof outputstring.Data === 'string') {
                comp.CrossTabOutPutEntireData = JSON.parse(outputstring.Data);
              } else if (Array.isArray(outputstring.Data)) {
                comp.CrossTabOutPutEntireData = outputstring.Data;
              } else {
                console.error('Unexpected outputstring.Data format for EntireData:', typeof outputstring.Data);
                comp.CrossTabOutPutEntireData = [];
              }
              comp.normalizeNestedChildFields(comp.CrossTabOutPutEntireData);
              if (comp.mcode == 2 || comp.mcode == 3) {
                comp.cleanBrandAnalysisChild(comp.CrossTabOutPutEntireData);
                comp.cleanBrandAnalysisChild(comp.CrossTabOutPutData);
              }
              let chartType = 'column';
              chartType = comp.isTranspose ? 'column' : 'bar';
              if (comp.CrossTabOutPutEntireData.length > 0) {
                comp.CrossTabOutPutData = comp.CrossTabOutPutEntireData;
              }
              comp.isTranspose = false;
              comp.insufficientBase = comp.insufficentBase || '';
              comp.DefaultConfidenceLevel = 'None';
              comp.isAnswerBase = false;
              comp.isSampleSize = false;
              comp.isWeightedLabel = false;
              comp.LowBase = comp.lowBase || '';
              comp.isPairWise = false;
              comp.DefaultSampleSize = 'None';
              comp.threshold = '';
              comp.selectedIndex = '';
              comp.isIndex = false;
              let fallbackSelected = comp.selectedWeight;
              if ((comp.mcode === 2 || comp.mcode === 3 || comp.promptData.mcode == 2 || comp.promptData.mcode == 3) && !comp.promptData.weight && !comp.promptData.Weight) {
                  const primary = comp.WeightDropDownList?.find((w: any) => w.Label === 'Primary Weight' || w.responseCode?.toLowerCase() === 'weightb');
                  if (primary) fallbackSelected = primary.responseCode;
              }
              comp.selectedWeight = comp.promptData.weight || comp.promptData.Weight || fallbackSelected;
              if (comp.WeightDropDownList && comp.WeightDropDownList.length > 0) {
                const matchedWeight = comp.WeightDropDownList.find((w: any) => w.responseCode === comp.selectedWeight);
                if (matchedWeight) {
                  if (matchedWeight.responseCode?.toLowerCase() === 'weightb') comp.DefaultWeight = 'Primary Weight';
                  else if (matchedWeight.responseCode?.toLowerCase() === 'weight') comp.DefaultWeight = 'Secondary Weight';
                  else if (matchedWeight.responseCode?.toLowerCase() === 'unweighted') comp.DefaultWeight = 'Unweighted';
                  else comp.DefaultWeight = matchedWeight.Label;
                } else {
                  comp.DefaultWeight = (comp.selectedWeight === 'Unweighted' || comp.selectedWeight === 'unweighted') ? 'Unweighted' : comp.selectedWeight;
                }
              } else {
                comp.DefaultWeight = (comp.selectedWeight === 'Unweighted' || comp.selectedWeight === 'unweighted') ? 'Unweighted' : comp.selectedWeight;
              }
              comp.isBrandAnalysis = comp.promptData.mcode == 2 ? true : false;


              if (!comp.isTotal) {
                const preFilterCount = comp.CrossTabOutPutEntireData.length;
                comp.CrossTabOutPutData = comp.CrossTabOutPutEntireData.filter(
                  (x: any) => {
                    return !x.isTotal;
                  }
                );
              } else {
              }

              if (comp.isIndex && comp.selectedIndex != '') {
                comp.manipulateIndexData();
              }

              comp.BindTableOutput(comp.CrossTabOutPutData);
              comp.sortOutputData();


              comp.isCreateBtnClick = true;
              if (comp.isTranspose) {
                comp.handleOutputdataObjectOnTransposeSelect();
              }
              if (
                comp.OutputdataObject.Row.length != 0 &&
                comp.OutputdataObject.Column.length != 0
              ) {
                comp.displayTable = true;
                setTimeout(() => MarkdownPayloadHelper.storeTableAsMarkdown(comp), 300);
              }
              comp.updateChartDisablingFlags();


              comp.crossTabService.setCrossTabOuputData(
                comp.CrossTabOutPutData
              );
              if (comp.isLineChartClick) {
                comp.onTrendChartIconClick();
              } else if (
                comp.isVerticalGroupBarChartClick ||
                comp.isVerticalBarChartClick
              ) {
                comp.VerticalChartClick(comp.chartType);
              } else if (comp.isVerticalStackChartClick) {
                comp.onVerticalStackBarChartIconClick(comp.chartType);
              }
              comp.is_Time_Period_Present = true;

            } else {
              console.error('==== NO VALID RESPONSE DATA ====');
              console.error('data.response is null or HasValidData is false');
              console.error('Cannot populate CrossTabOutPutData');
              comp.CrossTabOutPutData = [];
            }

            comp.isInsightspopupOpen = false;
            comp.arktabaccess = true;
            comp.isInsightsBtnEnabled = true;


            if (comp.CrossTabOutPutData && comp.CrossTabOutPutData.length > 0) {
              const hasNonZeroData = comp.CrossTabOutPutData.some((item: any) => {
                const hasValue = (item.totalWeightedShare > 0 ||
                  item.totalUnweightedShare > 0 ||
                  item.answerWeightedShare > 0 ||
                  item.answerUnweightedShare > 0 ||
                  item.value > 0);
                return hasValue;
              });


              if (!hasNonZeroData) {
                comp.insightsVal = isChatResponse ? (getInsights || ['No insights available for this selection.']) : ['Metric values are zero or null for the selected criteria.'];
              }

              const markdownData = comp.buildCrosstabMarkdownForAPI();

              if (isChatResponse) {
                if (getInsights) {
                  comp.openInsights(isChatResponse, getInsights);
                } else {
                  comp.insightsVal = ['Insights not available for this historical chat.'];
                  comp.isInsightspopupOpen = true;
                  comp.arktabaccess = false;
                }
              } else {
                // Use cached insights if available (from pre-fetch)
                const cached = (comp.multiViewInsightsCache[msgId] || {})[comp.mcode];
                if (cached) {
                  comp.insightsVal = cached.insightsVal;
                  comp.updateMarkdownTextRealTime(cached.qnaText);
                  comp.isInsightspopupOpen = true;
                  comp.isInsightsStreaming = false;
                  comp.isStreamFinished = true;
                  comp.statusQueue = []; // Clear queue to instantly show post-stream message
                  setTimeout(() => {
                    comp.stopAiLoader();
                    comp.arktabaccess = false;
                  }, 1500);

                  if (!isChatResponse) {
                    // Insights already pushed in Step 2 bundling if cached
                    comp.saveChatList(
                      targetBlock ? targetBlock.question : (comp.chatPreviewBlocks.length > 0 ? comp.chatPreviewBlocks[comp.chatPreviewBlocks.length - 1].question : comp.inputValue),
                      comp.chatResponse,
                      cached.qnaText || comp.getFirstTwoInsightsAsPlainText() || 'Insights ready.',
                      targetBlock?.messageId
                    );
                  }
                } else if (!comp.failedCase) {
                  console.warn(`[ORCHESTRATION] No cached insights for mcode: ${comp.mcode}. Triggering fallback.`);

                  // Unblock the main UI wrapper immediately by setting the selected message ID to the active block
                  if (targetBlock && targetBlock.messageId) {
                    comp.selectedMessageId = targetBlock.messageId;
                  }

                  setTimeout(() => {
                    comp.callInsightsAPI(comp.mcode, markdownData, comp.currentQuestion, {}, targetBlock);
                  }, 100);
                }
              }
            } else {
              console.error('[Insights Check] CrossTabOutPutData is empty, cannot generate insights');
              comp.insightsVal = ['No output data available for this question.'];
              comp.setFailedCaseState(true);
              comp.isInsightspopupOpen = true;
              comp.isStreamFinished = false;
              comp.isInsightsStreaming = false;
              comp.arktabaccess = false;
              if (!isChatResponse) {
                comp.saveChatList(targetBlock ? targetBlock.question : (comp.currentQuestion || 'Analytics Question'), comp.chatResponse, 'No output data available for this question.', targetBlock?.messageId);
              }
            }
            if (isChatResponse || !comp.CrossTabOutPutData || comp.CrossTabOutPutData.length === 0) {
              comp.arktabaccess = false;
            }
            comp.LoadRibbonData(comp.filteredlist);
            // OLD code - 08/12/2025
          }

          if (comp.mcode == 99) {
            comp.insightsVal = comp.fallbackMessage();
            comp.setFailedCaseState(true);
            comp.errorHeader = 'Analysis Status:';
            comp.isStreamFinished = false;
            comp.isInsightsStreaming = false;
            comp.arktabaccess = false;
            comp.isInsightspopupOpen = true;
            comp.currentView = 'chat';

            if (!isChatResponse) {
              comp.pushChatResponseEntry('failedCase', true);
              comp.pushChatResponseEntry('errorHeader', comp.errorHeader);
            }

            const errorMessage = comp.fallbackMessage();
            const shortErrorMessage = 'I encountered an issue processing your query. Please try again.';
            if (comp.chatPreviewBlocks.length > 0) {
              comp.updateQuestionAnswerForSelectedMessage(shortErrorMessage);
            }

            // Removed saveChatList to prevent recording mcode 99 errors in history
            comp.isHistorySaving = false;
            comp.isDiagnostic = true;

            if (comp.promptData && typeof comp.promptData === 'object' && Object.keys(comp.promptData).length > 0) {
              comp.apiService.getOutputData(
                comp.inputValue,
                comp.landingdata.client_code,
                comp.landingdata.study,
                [],
                comp.promptData,
                comp.weightFilter
              ).subscribe(
                (outputResponse: any) => {
                  comp.promptData = outputResponse;
                  comp.insightsVal = outputResponse.p_user_response || comp.fallbackMessage();
                  comp.Widget_Id = outputResponse.Widget_ID;
                  comp.isTotalBase = outputResponse.isTotalBase;
                  comp.setFailedCaseState(true);
                  comp.errorHeader = 'Analysis Status:';
                  comp.isInsightspopupOpen = true;
                  comp.arktabaccess = false;
                  comp.isDiagnostic = true;

                  if (!isChatResponse) {
                    comp.pushChatResponseEntry('failedCase', true);
                    comp.pushChatResponseEntry('errorHeader', comp.errorHeader);
                  }
                },
                (error: any) => {
                  console.error('getOutputData error:', error);
                  comp.arktabaccess = false;
                  comp.inputValue = '';
                }
              );
            } else {
              comp.arktabaccess = false;
              comp.inputValue = '';
            }
          }

          if (targetBlock) {
            targetBlock.CrossTabOutPutData = [...(comp.CrossTabOutPutData || [])];
            targetBlock.OutputdataObject = comp.OutputdataObject ? JSON.parse(JSON.stringify(comp.OutputdataObject)) : null;
            targetBlock.CrossTabOutPutEntireData = [...(comp.CrossTabOutPutEntireData || [])];
            targetBlock.dataObject = comp.dataObject ? JSON.parse(JSON.stringify(comp.dataObject)) : null;
            targetBlock.IndexData = comp.getLastLevelColumnNestingValues(targetBlock) || [];
            targetBlock.variableTabData = [...(comp.variableTabData || [])];
            targetBlock.WeightDropDownList = [...(comp.WeightDropDownList || [])];
            targetBlock.SampleSizeList = [...(comp.SampleSizeList || [])];

            // [DATA ISOLATION] Sync all UI and analytical settings to keep the control bar in sync
            targetBlock.mcode = comp.mcode;
            targetBlock.is_sigtesting = comp.is_sigtesting;
            targetBlock.is_Time_Period_Present = comp.is_Time_Period_Present;
            targetBlock.isIndex = comp.isIndex;
            targetBlock.isSig = comp.isSig;
            targetBlock.isTranspose = comp.isTranspose;
            const __ribbonFlags = comp.computeStudyRibbonFlags();
            targetBlock.is_TransposeBtn = comp.is_TransposeBtn || __ribbonFlags.transposeAvailable;
            targetBlock.is_confidenceLevel = (comp.is_confidenceLevel && comp.is_confidenceLevel.length)
              ? [...comp.is_confidenceLevel]
              : [...__ribbonFlags.confidenceLevels];
            targetBlock.SignificanceVsReference = comp.SignificanceVsReference;
            targetBlock.selectionSummary = comp.selectionSummary;
            targetBlock.DefaultConfidenceLevel = comp.DefaultConfidenceLevel;
            targetBlock.DefaultSampleSize = comp.DefaultSampleSize;
            targetBlock.DefaultReferenceLevel = comp.DefaultReferenceLevel;
            targetBlock.selectedWeight = comp.selectedWeight;
            targetBlock.DefaultWeight = comp.DefaultWeight;
            targetBlock.DefaultBase = comp.DefaultBase;
            targetBlock.isTotalBase = comp.isTotalBase;
            targetBlock.isAnswerBase = comp.isAnswerBase;
            targetBlock.isSampleSize = comp.isSampleSize;

            targetBlock.IndexData = comp.getLastLevelColumnNestingValues(targetBlock) || [];

            // If IndexData has options, enable reference. Otherwise disable it.
            targetBlock.isDisableReference = targetBlock.IndexData?.length <= 1;
            targetBlock.isWeightedLabel = comp.isWeightedLabel;
            targetBlock.isTableClick = comp.isTableClick;
            targetBlock.isChartView = comp.isChartView;
            targetBlock.isVerticalGroupBarChartClick = comp.isVerticalGroupBarChartClick;
            targetBlock.isVerticalBarChartClick = comp.isVerticalBarChartClick;
            targetBlock.isVerticalStackChartClick = comp.isVerticalStackChartClick;
            targetBlock.isLineChartClick = comp.isLineChartClick;
            targetBlock.DefaultDataDropDown = comp.DefaultDataDropDown || 'Data';
            targetBlock.disableStackChart = comp.disableStackChart;
            targetBlock.disableLineChart = comp.disableLineChart;
            targetBlock.showStackTooltip = comp.showStackTooltip;
            targetBlock.showLineTooltip = comp.showLineTooltip;
            targetBlock.showBarTooltip = comp.showBarTooltip;
            targetBlock.showMainTooltip = comp.showMainTooltip;
            comp.updateChartDisablingFlags(targetBlock);
            // [REDIRECT ISOLATION] Save per-block payloads so redirectToModule uses the correct question's data
            if (comp.lastCrosstabPayload) targetBlock.crosstabPayload = comp.lastCrosstabPayload;
            if (comp.lastBrandAnalysisPayload) targetBlock.brandAnalysisPayload = comp.lastBrandAnalysisPayload;
            if (comp.lastSnapshotPayload) targetBlock.snapshotPayload = comp.lastSnapshotPayload;
          }
        },
        error: (error: any) => {
          console.error('[ORCHESTRATION ERROR] Orchestration API failed:', error);
          comp.stopAiLoader();
          comp.arktabaccess = false;
          comp.isInsightsStreaming = false;

          let errorMessage = 'We encountered an issue while processing your request. Please try again later or contact support if the issue persists.';
          let shortErrorMessage = 'Unable to process request. Please try again.';
          comp.errorHeader = 'Analysis Error:';

          if (typeof error === 'string' && error.includes('All analytical agents failed')) {
            errorMessage = 'We couldn\'t generate a complete analysis for this request. Please try refining your question or selecting different parameters.';
            shortErrorMessage = 'Analysis could not be completed. Please refine your question.';
            comp.errorHeader = 'Analysis Unavailable:';
          } else if (error.status === 401) {
            errorMessage = 'Session expired. Please refresh the page to continue asking questions in this chat.';
            shortErrorMessage = 'Session expired. Please refresh page.';
            comp.errorHeader = 'Authentication Error:';
            console.error('[ORCHESTRATION ERROR] 401 - Authentication failed, likely expired session for old chat');
          } else if (error.status === 403) {
            errorMessage = 'Access denied. You do not have permission to perform this action.';
            shortErrorMessage = 'Access denied. Permission required.';
            comp.errorHeader = 'Access Denied:';
          } else if (error.status === 404) {
            errorMessage = 'The requested service could not be found. Please contact your administrator if this issue persists.';
            shortErrorMessage = 'Service connection failed.';
            comp.errorHeader = 'Invalid Request:';
          } else if (error.status === 500) {
            errorMessage = 'We\'re currently experiencing an internal server error. Please contact your administrator for assistance.';
            shortErrorMessage = 'Internal server error.';
            comp.errorHeader = 'Server Error:';
          } else if (error.status === 0) {
            errorMessage = 'It looks like you\'re offline or experiencing a network issue. Please check your internet connection and try again.';
            shortErrorMessage = 'Network connection interrupted.';
            comp.errorHeader = 'Network Error:';
          }

          if (targetBlock) {
            comp.selectedMessageId = targetBlock.messageId || null;
          } else if (comp.chatPreviewBlocks.length > 0) {
            comp.selectedMessageId = comp.chatPreviewBlocks[comp.chatPreviewBlocks.length - 1].messageId || null;
          }

          if (comp.chatPreviewBlocks.length > 0) {
            comp.updateQuestionAnswerForSelectedMessage(shortErrorMessage);
          } else if (!isChatResponse) {
            const tempMsgId = 'temp-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
            comp.selectedMessageId = tempMsgId;
            comp.chatPreviewBlocks.push({
              messageId: tempMsgId,
              question: comp.inputValue || 'Question',
              response: '',
              isLiked: null,
              isStarred: false,
              feedbackSaved: false,
              showLikeInput: false,
              likeReason: '',
              showDislikeInput: false,
              dislikeReason: '',
              QuestionAnswer: shortErrorMessage,
              isTrendActive: comp.isTrendActive,
              isExportspopup: false,
              sampleSizeDropdown: false,
              confidenceLevelDropDown: false,
              referenceLevelDropDown: false,
              baseDropDown: false,
              weightDropDown: false,
              isReference: false,
              isSig: true,
              isIndex: false,
              DefaultSampleSize: 'None',
              SignificanceVsReference: '',
              DefaultConfidenceLevel: '95%',
              DefaultReferenceLevel: 'None',
              selectedWeight: comp.selectedWeight || 'Weight',
              DefaultWeight: comp.DefaultWeight || 'Total Population',
              DefaultBase: comp.DefaultBase || 'Total Base',
              isTotalBase: true,
              isAnswerBase: true,
              is_sigtesting: true,
              is_Time_Period_Present: true,
              isTranspose: false,
              dataDropDown: false,
              DefaultDataDropDown: 'Data',
              isTableClick: true,
              isVerticalStackChartClick: false,
              isVerticalBarChartClick: false,
              isVerticalGroupBarChartClick: false,
              isLineChartClick: false,
              isChartView: true,
              disableStackChart: false,
              disableLineChart: false,
              showStackTooltip: false,
              showLineTooltip: false,
              showBarTooltip: false,
              showMainTooltip: false,
              StackFlag: false,
              displayTable: true,
              isTableClosing: false,
              isChartClosing: false
            });
          }

          if (!isChatResponse) {
            comp.pushChatResponseEntry('failedCase', true);
            comp.pushChatResponseEntry('noContentFlag', true);
            comp.pushChatResponseEntry('errorHeader', comp.errorHeader);
          }

          comp.insightsVal = errorMessage;
          comp.setFailedCaseState(true, targetBlock);
          comp.noContentFlag = true;
          comp.isInsightspopupOpen = true;
          const errorObj: InsightsModel = {
            header: comp.errorHeader || 'Error',
            message: errorMessage,
            show: true,
          };
          comp._insightsSerive.showInisghts(errorObj);

          // Removed saveChatList to prevent recording orchestration error questions in history
          comp.isHistorySaving = false;
          comp.currentView = 'chat';
        }
      });
    }
  }

  static identifyAvailableViews(rawData: any, parsedMemberResponses?: any) {
    const resp = parsedMemberResponses || rawData?.member_responses || rawData;
    const views: any[] = [];
    if (resp) {
      const findKey = (search: string) => Object.keys(resp).find(k => k.toLowerCase() === search.toLowerCase());
      if (findKey('snapshot')) views.push({ name: 'Snapshot', mcode: 1, key: 'snapshot' });

      const baKey = findKey('brand_analysis') || findKey('brandanalysis');
      if (baKey) {
        const baData = resp[baKey];
        if (baData && baData.Row !== null && baData.Column !== null) {
          views.push({ name: 'Brand Analysis', mcode: 2, key: baKey });
        }
      }
      const ctKey = findKey('crosstab');
      if (ctKey) {
        const ctData = resp[ctKey];
        if (ctData && ctData.Row !== null && ctData.Column !== null) {
          views.push({ name: 'Crosstab', mcode: 3, key: ctKey });
        }
      }
    } else {
      console.warn('[MULTI-VIEW] No member_responses found in rawData');
    }
    return views;
  }
}
